import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FormInput, Button, Loader } from "@src/components/atoms";
import Form from "@src/components/forms/Form";
import { REQUIRED_FIELD_MESSAGE } from "@src/constants/constants";
import AppUtils from "@src/utils/appUtils";
import SignolUtils from "@src/utils/signolUtils";
import { useAuthContext } from "@src/context/authContext";
import AuthHooks from "@src/context/hooks/authHooks";
import { changePasswordSchema } from "@src/components/forms/formHelpers";

const ChangePasswordForm = (props) => {
  const authStore = useAuthContext();
  const { setOpen } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChanged, setIsChanged] = useState(null);
  const [error, setError] = useState("");

  const mutationCallback = (status) => {
    setIsSubmitting(false);
    setIsChanged(status);
  };
  const mutation = AuthHooks.useChangePassword(mutationCallback);

  useEffect(() => {
    if (isChanged !== null) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, 2000);

      return () => clearTimeout(timer); // cleanup on unmount or isChanged change
    }
  }, [isChanged, setOpen]);

  const currentUser = authStore.loggedUser;
  const useSSO = currentUser && SignolUtils.getLoginSSO(currentUser.username);
  if (useSSO) {
    return (
      <Redirect to={`/dashboard/${authStore.activeRoleRoutePrefix}/profile`} />
    );
  }

  const handleSubmit = async (data) => {
    setError("");
    setIsSubmitting(true);
    try {
      mutation.mutate(data);
    } catch (err) {
      console.error("Password change error", err?.response?.data || err);
      setError(err?.response?.data?.message || "Failed to change password.");
    }
  };

  return (
    <div>
      {isChanged === null && (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-2xl">
          <h3 className="mb-6 text-center">Change Password</h3>
          <Form
            onSubmit={handleSubmit}
            schema={changePasswordSchema}
            options={{
              defaultValues: {
                passwordOld: "",
                passwordNew: "",
                passwordNewConfirm: "",
              },
            }}
            className="space-y-6"
          >
            {(methods) => (
              <>
                <FormInput
                  label="Current Password"
                  name="passwordOld"
                  type="password"
                  placeholder="••••••••"
                  error={methods.formState.errors.passwordOld?.message}
                  textColor="primary"
                />
                <FormInput
                  label="New Password"
                  name="passwordNew"
                  type="password"
                  placeholder="••••••••"
                  error={methods.formState.errors.passwordNew?.message}
                  textColor="primary"
                  showPasswordRules
                />
                <FormInput
                  label="Confirm New Password"
                  name="passwordNewConfirm"
                  type="password"
                  placeholder="••••••••"
                  error={methods.formState.errors.passwordNewConfirm?.message}
                  textColor="primary"
                />
                <div className="flex justify-center">
                  <Button submit loading={!!isSubmitting}>
                    <div className="px-4">Confirm</div>
                  </Button>
                </div>
                {error && (
                  <div className="mt-4 text-xs italic text-red-500 text-center">
                    {error}
                  </div>
                )}
                <div className="text-center pt-4">
                  <p
                    onClick={() => setOpen(false)}
                    className="text-primary-500 underline text-sm cursor-pointer"
                  >
                    Go Back
                  </p>
                </div>
              </>
            )}
          </Form>
        </div>
      )}
      {isChanged === false && (
        <>
          <div className="flex flex-col h-full w-full justify-center gap-4 text-center">
            <div className="text-2xl font-extrabold py-2">Update failed.</div>
            <div className="text-base font-medium p-6">
              We couldn't update your password at this time. Please try again
              later.
            </div>
          </div>
        </>
      )}
      {isChanged === true && (
        <>
          <div className="flex flex-col h-full w-full justify-center gap-4 text-center">
            <div className="text-2xl font-extrabold py-2">Thanks.</div>
            <div className="text-base font-medium p-6">
              Your password have been updated.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangePasswordForm;
